
import { useState } from "react";
import { createSubmission } from "@/lib/submissions";
import { incrementFunnelMetric } from "@/lib/analytics";
import { toast } from "sonner";
import type { FunnelData } from "@/components/FunnelBuilder/types";

export const useFunnelForm = (funnel: FunnelData) => {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, string>>({});

  const handleInputChange = (elementId: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [elementId]: value
    }));
  };

  const handleFormButtonClick = async () => {
    if (funnel?.id) {
      try {
        await incrementFunnelMetric(funnel.id, 'formClicks');
      } catch (error) {
        console.error("Error updating form clicks:", error);
      }
    }
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!funnel.id) return;

    setIsSubmitting(true);
    try {
      const formData: Record<string, any> = {};
      funnel.formElements.forEach(element => {
        formData[element.id] = formValues[element.id] || '';
      });

      await createSubmission(funnel.id, formData);
      await incrementFunnelMetric(funnel.id, 'submissions');
      
      setIsSubmitted(true);
      setShowForm(false);
      setFormValues({});

      if (funnel.emailConfig?.enabled && funnel.emailConfig?.notifyOwner) {
        console.log("Owner notification email would be sent for submission:", {
          funnelName: funnel.siteName,
          formData
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    showForm,
    setShowForm,
    isSubmitting,
    isSubmitted,
    formValues,
    handleInputChange,
    handleFormButtonClick,
    handleSubmit
  };
};
